import InvoiceListOptions from '@/components/Invoice/InvoiceListOptions.vue'
import FundsLocationBadge from '@/components/Payment/FundsLocationBadge.vue'
import { Banknote, CircleUserRound, Clock, FileText, Info, Store } from 'lucide-vue-next'
import StatusBadge from 'pejer/src/components/StatusBadge.vue'
import { Groups, JSSchema } from 'pejer/src/tables'
import {
  CurrencyList,
  MoneyFormatter,
  PaymentEnvironmentFormatter,
  PaymentEnvironmentList,
  PaymentStatuses,
  PaymentStatusFormatter,
} from 'pejer/src/tables/Global'
import { ColorPalette } from 'pejer/src/tables/helpers'
import { h } from 'vue'
import { PaymentsEmbed } from './Payments'
import { paymentStatusLabels } from './paymentStatusLabels'
export type NotificationType =
  | 'PaymentReminder'
  | 'OverdueNotice'
  | 'DebtCollectionNotice'
  | 'PaymentRequest'
  | 'EnforcementNotice'
export namespace InvoicesEmbed {
  export interface Full extends ListItem {
    moneySplit: PaymentsEmbed.MoneySplit[]
  }

  export interface ListItem {
    paymentID: string
    paymentReferenceID: string
    connectedAccountIDSeller: string
    externalPaymentReferenceID: string
    description: string
    externalObjectIDTag: string
    environment: string
    paymentStatus: (typeof PaymentStatuses)[any]
    paymentGateway: string
    paymentStatusUpdateTime: string
    paymentProvider: string
    currency: string
    grossAmount: number
    insertTime: string
    canActivatePersonalDebtCollection: boolean
    hasApprovedPersonalDebtCollectionAgreement: boolean
    latestNotification: {
      notificationType: NotificationType
      sentTime: string
      actionDueDate: string | null
    } | null

    customer: Customer
    kidNorwayInvoice: KidNorwayInvoice
    connectedAccountSeller: ConnectedAccountSeller
    fundsLocation?: PaymentsEmbed.FundsLocation
  }

  export interface KidNorwayInvoice {
    kid: string
    accountNumber: string
    dueDate: string
    paidAmount: number

    customerType: string
    customerName: string
    customerAddress: string
    customerPostalCode: string
    customerCity: string
    customerEmail: string
    debtCollectionActivated: boolean

    invoiceUrl: string
    receiverIsDirectPaymentAccount: boolean
  }

  export interface Customer {
    customerExternalID: string
    customerGuid: string
    customerType: string
    email: string
    mobile: string
    mobilePrefix: string
    name: string
    updateTime: string
  }

  export interface ConnectedAccountSeller {
    connectedAccountID: string
    businessName: string
    business_TaxID: string
    countryCode: string
    name: string
  }

  export interface MoneySplitRequest {
    splitID: number
    amount: number
  }

  export interface AdjustOrRefundPaymentRequest {
    paymentReferenceID: string
    moneySplit: MoneySplitRequest[]
    refundDescription: string
  }
}

export const InvoicesEmbedGroups = {
  Konto: {
    icon: Store,
    color: ColorPalette.pink,
  },
  Kunde: {
    icon: CircleUserRound,
    color: ColorPalette.green,
  },
  Faktura: {
    icon: FileText,
    color: ColorPalette.brown,
    isMainGroup: true,
  },
  Beløp: {
    icon: Banknote,
    color: ColorPalette.green,
  },
  Forfall: {
    icon: Clock,
    color: ColorPalette.black,
  },
  Status: {
    icon: Info,
    color: ColorPalette.brown,
  },
} satisfies Groups

export const InvoicesEmbedListSchema: JSSchema<InvoicesEmbed.ListItem, typeof InvoicesEmbedGroups> =
  {
    name: 'Partial Payments Embed',
    title: 'Partial Payments',
    groups: InvoicesEmbedGroups,
    fields: {
      paymentID: {
        type: 'string',
        humanReadable: 'Fakturanummer',
        meta: {
          group: 'Faktura',
        },
      },
      fundsLocation: {
        type: 'string',
        humanReadable: 'Betales til',
        meta: {
          group: 'Faktura',
        },
        specificCell:
          () =>
          ({ row }) =>
            h(FundsLocationBadge, { payment: row.original, hideMethod: true }),
      },
      paymentReferenceID: {
        type: 'string',
        humanReadable: 'Referanse-ID',
        meta: {
          group: 'Faktura',
        },
        // formattedCell: (value: string) => {
        //   return h(
        //     ColumnLink,
        //     {
        //       content: value,
        //       id: value,
        //       pathName: 'payment',
        //     },
        //     () => textWithIconCell(Banknote, value)
        //   )
        // },
      },
      externalPaymentReferenceID: {
        type: 'string',
        humanReadable: 'Ekstern referanse-ID',
        meta: {
          group: 'Faktura',
          hideFromTable: true,
        },
      },
      connectedAccountIDSeller: {
        humanReadable: 'Selger-ID',
        relation: 'connectedAccounts.connectedAccountID',
        meta: {
          group: 'Konto',
          hideFromTable: true,
        },
      },
      description: {
        type: 'string',
        humanReadable: 'Beskrivelse',
        meta: {
          group: 'Faktura',
        },
        sensitive: true,
        // inSearchSelect: true,
      },
      externalObjectIDTag: {
        type: 'string',
        humanReadable: 'Ekstern objekt-ID-tag',
        meta: {
          group: 'Faktura',
        },
      },
      environment: {
        type: 'enum',
        humanReadable: 'Driftsmiljø',
        options: PaymentEnvironmentList,
        formatter: PaymentEnvironmentFormatter,
        meta: {
          group: 'Faktura',
          hideFromTable: true,
        },
      },
      paymentStatus: {
        type: 'enum',
        humanReadable: 'Status',
        options: PaymentStatuses,
        formatter: PaymentStatusFormatter,
        meta: {
          group: 'Faktura',
        },
        formattedCell: (value: any) => {
          return h(StatusBadge, {
            status: value,
            label: paymentStatusLabels[value as keyof typeof paymentStatusLabels],
          })
        },
      },
      paymentGateway: {
        type: 'string',
        humanReadable: 'Betalingsmetode',
        meta: {
          group: 'Faktura',
        },
        metaType: 'badge',
      },
      paymentStatusUpdateTime: {
        type: 'date',
        humanReadable: 'Betalingsstatus sist endret',
        meta: {
          group: 'Faktura',
        },
      },
      currency: {
        type: 'enum',
        humanReadable: 'Valuta',
        options: CurrencyList,
        meta: {
          group: 'Faktura',
        },
      },
      grossAmount: {
        type: 'number',
        humanReadable: 'Beløp',
        formatter: MoneyFormatter,
        metaType: 'money',
        meta: {
          group: 'Beløp',
          isMain: true,
        },
        //! TODO: get the productline amount
      },
      insertTime: {
        type: 'date',
        humanReadable: 'Opprettet',
        meta: {
          group: 'Faktura',
        },
        isDefaultSortColumn: true,
      },
      customer: {
        type: 'object',
        humanReadable: 'Kunde',
        meta: {
          group: 'Kunde',
        },
        values: {
          customerExternalID: {
            type: 'string',
            humanReadable: 'Ekstern ID',
            meta: {
              group: 'Kunde',
              hideFromTable: true,
            },
          },
          customerGuid: {
            type: 'string',
            humanReadable: 'GUID',
            meta: {
              group: 'Kunde',
              hideFromTable: true,
            },
          },
          customerType: {
            type: 'string',
            humanReadable: 'Kundetype',
            meta: {
              group: 'Kunde',
            },
          },
          email: {
            type: 'string',
            humanReadable: 'E-post',
            meta: {
              group: 'Kunde',
            },
          },
          mobile: {
            type: 'string',
            humanReadable: 'Mobil',
            meta: {
              group: 'Kunde',
            },
          },
          mobilePrefix: {
            type: 'string',
            humanReadable: 'Mobilprefiks',
            meta: {
              group: 'Kunde',
            },
          },
          name: {
            type: 'string',
            humanReadable: 'Opprettet av',
            meta: {
              group: 'Kunde',
            },
            inSearchSelect: true,
          },
          updateTime: {
            type: 'date',
            humanReadable: 'Oppdateringstid',
            meta: {
              group: 'Kunde',
              hideFromTable: true,
            },
          },
        },
      },
      connectedAccountSeller: {
        type: 'object',
        humanReadable: 'Selger',
        meta: {
          group: 'Konto',
        },
        values: {
          connectedAccountID: {
            type: 'string',
            humanReadable: 'ID',
            meta: {
              group: 'Konto',
              hideFromTable: true,
            },
          },
          businessName: {
            type: 'string',
            humanReadable: 'Bedriftsnavn',
            meta: {
              group: 'Konto',
            },
          },
          business_TaxID: {
            type: 'string',
            humanReadable: 'Organisasjonsnummer',
            meta: {
              group: 'Konto',
            },
          },
          countryCode: {
            type: 'string',
            humanReadable: 'Landskode',
            meta: {
              group: 'Konto',
            },
          },
          name: {
            type: 'string',
            humanReadable: 'Kontonavn',
            meta: {
              group: 'Konto',
              isMain: true,
            },
          },
        },
      },
      kidNorwayInvoice: {
        type: 'object',
        humanReadable: 'Faktura',
        meta: {
          group: 'Faktura',
        },
        values: {
          kid: {
            type: 'string',
            humanReadable: 'KID',
            meta: {
              group: 'Faktura',
            },
          },
          accountNumber: {
            type: 'string',
            humanReadable: 'Kontonummer',
            meta: {
              group: 'Faktura',
            },
          },
          dueDate: {
            type: 'date',
            humanReadable: 'Forfallsdato',
            meta: {
              group: 'Forfall',
            },
          },
          paidAmount: {
            type: 'number',
            humanReadable: 'Betalt',
            formatter: MoneyFormatter,
            metaType: 'money',
            meta: {
              group: 'Beløp',
            },
          },
          customerName: {
            type: 'string',
            humanReadable: 'Kundenavn på faktura',
            meta: {
              group: 'Kunde',
              isMain: true,
            },
          },
          customerType: {
            type: 'string',
            humanReadable: 'Kundetype på faktura',
            meta: {
              group: 'Kunde',
            },
          },
          customerAddress: {
            type: 'string',
            humanReadable: 'Kundeadresse på faktura',
            meta: {
              group: 'Kunde',
            },
          },
          customerPostalCode: {
            type: 'string',
            humanReadable: 'Postnummer på faktura',
            meta: {
              group: 'Kunde',
            },
          },
          customerCity: {
            type: 'string',
            humanReadable: 'By på faktura',
            meta: {
              group: 'Kunde',
            },
          },
          customerEmail: {
            type: 'string',
            humanReadable: 'Sendt til',
            meta: {
              group: 'Kunde',
            },
          },
          debtCollectionActivated: {
            type: 'boolean',
            humanReadable: 'Automatisk betalingsoppfølging',
            meta: {
              group: 'Faktura',
            },
          },
        },
      },
    },
    actions: {
      adjust: {
        cell: ({ row, emits }) => {
          return h(InvoiceListOptions, {
            invoice: row.original,
            'onUpdate:close': emits.reload,
          })
        },
      },
    },
  }

export const InvoicesEmbedFullSchema: JSSchema<InvoicesEmbed.Full, typeof InvoicesEmbedGroups> = {
  name: 'Partial Payments Embed Full',
  title: 'Partial Payment',
  groups: InvoicesEmbedGroups,
  fields: {
    ...(InvoicesEmbedListSchema.fields as unknown as JSSchema<
      InvoicesEmbed.Full,
      typeof InvoicesEmbedGroups
    >['fields']),
    moneySplit: {
      type: 'array',
      humanReadable: 'Delt beløp',
      meta: {
        group: 'Faktura',
      },
      values: {
        splitID: {
          type: 'number',
          humanReadable: 'Split-ID',
        },
        amount: {
          type: 'number',
          humanReadable: 'Beløp',
          formatter: MoneyFormatter,
          metaType: 'money',
        },
        connectedAccountExternalReferenceID: {
          type: 'string',
          humanReadable: 'Kontoens ekstern referanse ID',
        },
        connectedAccountID: {
          type: 'string',
          humanReadable: 'Konto-ID',
        },
        description: {
          type: 'string',
          humanReadable: 'Beskrivelse',
        },
        isFee: {
          type: 'boolean',
          humanReadable: 'Er gebyr',
        },
        paymentReferenceID: {
          type: 'string',
          humanReadable: 'Betalingsreferanse-ID',
        },
        insertTime: {
          type: 'date',
          humanReadable: 'Opprettet',
        },
        externalID: {
          type: 'string',
          humanReadable: 'Ekstern ID',
        },
      },
    },
  },
}
